import { ConfiguracaoConciliacao } from "@/models/conciliation-config.model";
import { ConciliacaoStatus } from "@/models/consignado/propostaFinanceira.model";
import { formatDateWithNoHours } from "@/utils/formatDateWithNoHours";
import { TimeIcon } from "@chakra-ui/icons";
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Checkbox,
  Flex,
  Icon,
  ListItem,
  Spinner,
  Text,
  UnorderedList,
} from "@chakra-ui/react";
import { useMemo } from "react";
import { FaCheckCircle } from "react-icons/fa";
import { PiWarningCircleFill } from "react-icons/pi";
import Pagination from "@/components/FinancialShared/Pagination";

export interface EtapaConciliacaoStatusItem {
  etapa: string;
  status: ConciliacaoStatus;
  observacao?: string;
  createdAt?: string | Date;
  updatedAt?: string | Date;
}

export type ProposalSidebarItem = {
  id: string;
  shortHash: string;
  numeroCcb: string;
  valorLiberado: number;
  valorParcela?: number;
  quantidadeParcela?: number;
  valorSeguro?: number;
  valorIof?: number;
  valorDesconto?: number;
  processorName?: string;
  insuranceCompanyName?: string;
  dataCriacaoProposta: string;
  statusConciliacao: ConciliacaoStatus;
  etapasConciliacao?: EtapaConciliacaoStatusItem[];
  // relatorios: {tipo: string, status: ConciliacaoStatus, propostaFinanceiraId: string }[]
};

const MAX_MASS_RECONCILIATION_ITEMS = 10;

type ConciliationSidebarType = {
  proposals: ProposalSidebarItem[];
  totalItems: number; 
  selectedProposalId: string;
  setSelectedProposalId: React.Dispatch<React.SetStateAction<string>>;
  selectedProposalIds: string[];
  onToggleProposalSelection: (proposalId: string, checked: boolean) => void;
  onReconcileSelected: () => void;
  onPageChange: (page: number, pageSize: number) => void; 
  currentPage: number;
  pageSize: number;
  isLoading?: boolean;
  isReconcilingSelected?: boolean;
  configuracoes: ConfiguracaoConciliacao[];
};

export default function ConciliationSidebar({
  proposals,
  totalItems,
  selectedProposalId,
  setSelectedProposalId,
  selectedProposalIds,
  onToggleProposalSelection,
  onReconcileSelected,
  onPageChange,
  currentPage,
  pageSize,
  isLoading,
  isReconcilingSelected,
  configuracoes
}: ConciliationSidebarType) {
  const totalPages = Math.ceil(totalItems / pageSize) || 1;

  const handleSetSelectedProposalId = (proposalId: string) => {
    setSelectedProposalId(proposalId);
  };


  const hasProposals = proposals.length > 0;
  const hasSelectedProposals = selectedProposalIds.length > 0;
  const isAtSelectionLimit = selectedProposalIds.length >= MAX_MASS_RECONCILIATION_ITEMS;

  const dynamicSteps = useMemo(() => {
    if (!configuracoes || !Array.isArray(configuracoes)) return [];

    return [...configuracoes]
      .sort((a, b) => a.ordem - b.ordem)
      .map((config) => ({
        title: config.nome,
        status: ConciliacaoStatus.PENDENTE,
        rawConfig: config,
      }));
  }, [configuracoes]);

  return (
    <Card py={2} borderColor="gray.200" borderWidth="2px" borderRadius="lg">
      <CardHeader p={0} mb={2}>
        <Text textAlign="center" fontSize="large" fontFamily="Lexend Deca" fontWeight="500">
          Propostas
        </Text>
      </CardHeader>
      <CardBody p={0} maxH="60vh" overflowY="auto">
        {isLoading ? (
          <Flex justifyContent="center" alignItems="center" py={8}>
            <Spinner size="sm" mr={2} />
            <Text fontSize="sm">Carregando propostas...</Text>
          </Flex>
        ) : !hasProposals ? (
          <Flex justifyContent="center" alignItems="center" py={8} px={4}>
            <Text fontSize="sm" color="gray.500" textAlign="center">
              Nenhuma proposta encontrada para os filtros aplicados.
            </Text>
          </Flex>
        ) : (
          <UnorderedList listStyleType="none" m={0} px={4} spacing={4}>
            {proposals.map((proposal) => {
              return (
                <ListItem
                key={proposal.id}
                border="1px solid #ccc"
                _hover={{
                  backgroundColor: 'gray.100',
                  borderColor: 'primary.900',
                  borderWidth: '4px',
                  cursor: "pointer",
                  opacity: 0.8,
                }}
                borderRadius={10}
                onClick={() => handleSetSelectedProposalId(proposal.id)}
                width="100%"
                height="100%"
                borderColor={proposal.id === selectedProposalId ? "primary.900" : "gray.100"}
                borderWidth={proposal.id === selectedProposalId ? "4px" : "4px"}
                display="flex"
                flexDirection="column"
                p={2}
                backgroundColor={proposal.id === selectedProposalId ? "primary.500" : "white"}
                color="black"
                bg="white"
                style={{position: 'relative', }}
              >
              <Flex
                position="absolute"
                top={2}
                right={2}
                direction="column"
                alignItems="center"
                gap={1}
                color="black"
                onClick={(event: React.MouseEvent<HTMLDivElement>) => event.stopPropagation()}
              >
                {proposal.statusConciliacao === ConciliacaoStatus.CONCLUIDO && <Icon as={FaCheckCircle} color="green.500" />}
                {proposal.statusConciliacao === ConciliacaoStatus.DIVERGENTE && <Icon as={PiWarningCircleFill} color="red.500" />}
                {proposal.statusConciliacao === ConciliacaoStatus.PENDENTE && <Icon as={TimeIcon} color="orange.500" />}
                <Checkbox
                  isChecked={selectedProposalIds.includes(proposal.id)}
                  isDisabled={!selectedProposalIds.includes(proposal.id) && isAtSelectionLimit}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    onToggleProposalSelection(proposal.id, event.target.checked);
                  }}
                />
              </Flex>
                <Text fontFamily="Lexend Deca" fontWeight="500" fontSize="1rem" mb={1}>
                  <Text as="span" fontWeight="400">
                    {proposal.shortHash || "-"} - CCB: {proposal.numeroCcb}
                  </Text>
                </Text>

                <Text fontFamily="Lexend Deca" fontWeight="500" fontSize="0.9rem">
                  <Text as="span" fontWeight="400">
                    {new Intl.NumberFormat("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    }).format(proposal.valorLiberado)}
                  </Text>
                  {" - "}
                  <Text as="span" fontWeight="400">
                    {" "}
                    {formatDateWithNoHours(proposal.dataCriacaoProposta)}
                  </Text>
                </Text>
              </ListItem>
              );
            })}
          </UnorderedList>
        )}
      </CardBody>
      <CardFooter p={0} px={4} mt={3} display="flex" flexDirection="column">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          isQuerying={Boolean(isLoading)}
          onPageChange={(page) => onPageChange(page, pageSize)}
          onPageSizeChange={(size) => onPageChange(1, size)}
          pageSizeOptions={[10, 20, 50]}
        />

        <Button
          colorScheme="primary"
          size="sm"
          width="100%"
          onClick={onReconcileSelected}
          isLoading={isReconcilingSelected}
          isDisabled={!hasSelectedProposals || dynamicSteps.length < 1}
        >
          Conciliar selecionados
        </Button>

        {isAtSelectionLimit && (
          <Text fontSize="xs" color="orange.500" mt={2} textAlign="center">
            Limite de {MAX_MASS_RECONCILIATION_ITEMS} contratos selecionados.
          </Text>
        )}
      </CardFooter>
    </Card>
  );
}
